import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ShoppingBag, Zap, Phone, Plus, CheckCircle, ShoppingCart } from "lucide-react";
import { useCart } from "../context/CartContext";

const categories = [
  {
    id: "chargeurs",
    title: "Chargeurs",
    emoji: "⚡",
    desc: "Chargeurs officiels et compatibles pour tous vos appareils Apple",
    items: [
      { name: "Chargeur USB-C 20W", compat: "iPhone 15/14/13/12, iPad", price: "29" },
      { name: "Chargeur USB-C 30W", compat: "MacBook Air M1/M2, iPad Pro", price: "49" },
      { name: "Chargeur USB-C 67W", compat: "MacBook Pro 13/14, MacBook Air", price: "69" },
      { name: "MagSafe 15W", compat: "iPhone 12 et ultérieurs", price: "45" },
      { name: "Chargeur secteur 5W", compat: "iPhone (anciens modèles), AirPods", price: "19" },
    ],
  },
  {
    id: "cables",
    title: "Câbles",
    emoji: "🔌",
    desc: "Câbles de charge et de transfert de données certifiés",
    items: [
      { name: "Câble USB-C vers Lightning 1m", compat: "iPhone 5 à 14, AirPods, iPad", price: "19" },
      { name: "Câble USB-C vers USB-C 1m", compat: "iPhone 15, iPad Pro, MacBook", price: "19" },
      { name: "Câble USB-C vers MagSafe 3 2m", compat: "MacBook Air/Pro M2 et ultérieurs", price: "49" },
      { name: "Câble USB-A vers Lightning 2m", compat: "iPhone 5 à 14", price: "15" },
      { name: "Câble Thunderbolt 4 1m", compat: "MacBook Pro M1/M2/M3", price: "59" },
    ],
  },
  {
    id: "coques",
    title: "Coques & Protection",
    emoji: "🛡",
    desc: "Protégez votre iPhone avec des coques de qualité",
    items: [
      { name: "Coque transparente iPhone 15 Pro", compat: "iPhone 15 Pro", price: "24" },
      { name: "Coque MagSafe iPhone 15 series", compat: "iPhone 15 / 15 Plus / 15 Pro / Pro Max", price: "29" },
      { name: "Coque renforcée iPhone 14", compat: "iPhone 14 / 14 Plus / 14 Pro / Pro Max", price: "24" },
      { name: "Verre trempé iPhone 15", compat: "iPhone 15 / 15 Plus", price: "19" },
      { name: "Verre trempé iPhone 15 Pro/Max", compat: "iPhone 15 Pro / Pro Max", price: "22" },
      { name: "Film de protection écran", compat: "Tous modèles iPhone", price: "12" },
    ],
  },
  {
    id: "adaptateurs",
    title: "Adaptateurs",
    emoji: "🔁",
    desc: "Adaptateurs pour connecter vos accessoires",
    items: [
      { name: "Adaptateur USB-C vers HDMI", compat: "MacBook, iPad Pro, iPhone 15", price: "35" },
      { name: "Hub USB-C 7-en-1", compat: "MacBook Air/Pro, iPad Pro", price: "69" },
      { name: "Adaptateur USB-C vers USB-A", compat: "Tous Mac USB-C", price: "19" },
      { name: "Adaptateur Lightning vers 3.5mm", compat: "iPhone 7 et ultérieurs", price: "15" },
      { name: "Adaptateur USB-C vers SD Card", compat: "MacBook, iPad Pro", price: "29" },
    ],
  },
  {
    id: "watch",
    title: "Accessoires Apple Watch",
    emoji: "⌚",
    desc: "Bracelets et protections pour votre Apple Watch",
    items: [
      { name: "Bracelet Sport silicone 41mm", compat: "Apple Watch 41mm Series 4-9, SE", price: "19" },
      { name: "Bracelet Sport silicone 45mm", compat: "Apple Watch 45mm Series 4-9, SE", price: "19" },
      { name: "Bracelet cuir milanais 41mm", compat: "Apple Watch 41mm", price: "39" },
      { name: "Bracelet cuir milanais 45mm", compat: "Apple Watch 45mm / Ultra", price: "39" },
      { name: "Verre de protection Apple Watch", compat: "Series 4, 5, 6, 7, 8, 9, SE", price: "15" },
    ],
  },
];

const cardCls = "p-5 rounded-2xl border border-white/5 bg-[#121215] hover:border-white/10 transition-colors";

function AccItem({ item, catId, idx }) {
  const { addItem } = useCart();
  const [added, setAdded] = useState(false);
  const handleAdd = () => {
    addItem({
      id: `acc_${catId}_${idx}`,
      name: item.name,
      price: parseFloat(item.price),
      type: "accessoire",
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 1200);
  };
  return (
    <div className={cardCls} data-testid="accessory-item">
      <div className="flex items-start justify-between gap-2 mb-3">
        <p className="text-sm font-bold text-white leading-snug">{item.name}</p>
        <span className="text-lg font-black text-[#FF5C00] whitespace-nowrap flex-shrink-0">
          {item.price}€
        </span>
      </div>
      <p className="text-xs text-white/40 leading-relaxed mb-4">{item.compat}</p>
      <button
        onClick={handleAdd}
        data-testid={`add-acc-${catId}-${idx}`}
        className={`w-full flex items-center justify-center gap-1.5 text-xs font-semibold py-2 rounded-full transition-all duration-200 ${
          added
            ? "bg-[#34C759] text-white"
            : "bg-[#FF5C00]/10 hover:bg-[#FF5C00] text-[#FF5C00] hover:text-white border border-[#FF5C00]/20 hover:border-transparent"
        }`}
      >
        {added ? <CheckCircle className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
        {added ? "Ajouté au panier !" : "Ajouter au panier"}
      </button>
    </div>
  );
}

export default function AccessoiresPage() {
  useEffect(() => {
    document.title = "Accessoires Apple — Atelier du Mac Paris";
  }, []);
  const { addItem, setIsOpen, count } = useCart();

  return (
    <div className="bg-[#0A0A0C] min-h-screen">
      {/* Header */}
      <section className="pt-32 pb-10 max-w-6xl mx-auto px-6">
        <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-white/40 hover:text-white/60 mb-8 transition-colors">
          <ChevronRight className="w-3 h-3 rotate-180" />
          Retour à l'accueil
        </Link>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-2xl bg-[#FF5C00]/10 flex items-center justify-center">
            <ShoppingBag className="w-6 h-6 text-[#FF5C00]" />
          </div>
          <span className="text-xs font-bold uppercase tracking-widest text-[#FF5C00]">Boutique</span>
        </div>
        <h1 className="text-5xl sm:text-6xl font-black tracking-tighter text-white mb-4">
          Accessoires<br /><span className="text-[#FF5C00]">Apple</span>
        </h1>
        <p className="text-lg text-white/60 max-w-xl">
          Chargeurs, câbles, coques, adaptateurs et accessoires Apple Watch — disponibles en atelier au 9 Rue de Mont-Louis, Paris 11e.
        </p>
        <div className="mt-6 inline-flex items-center gap-2 text-sm text-white/40 bg-white/5 px-4 py-2 rounded-full">
          <Zap className="w-4 h-4 text-[#FF5C00]" />
          Disponibilité selon stock — appelez avant de vous déplacer : <a href="tel:+33611367002" className="text-white/70 hover:text-white transition-colors ml-1">06 11 36 70 02</a>
        </div>
      </section>

      {/* Anchors */}
      <section className="max-w-6xl mx-auto px-6 mb-10">
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => (
            <a
              key={c.id}
              href={`#${c.id}`}
              className="flex items-center gap-1.5 text-sm font-semibold text-white/50 hover:text-white bg-white/5 hover:bg-white/10 px-4 py-2 rounded-full transition-all"
            >
              <span>{c.emoji}</span> {c.title}
            </a>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="pb-24 max-w-6xl mx-auto px-6 space-y-16">
        {categories.map((cat) => (
          <div key={cat.id} id={cat.id} data-testid={`category-${cat.id}`}>
            <div className="flex items-start gap-3 mb-6">
              <span className="text-3xl">{cat.emoji}</span>
              <div>
                <h2 className="text-2xl font-black text-white">{cat.title}</h2>
                <p className="text-sm text-white/40 mt-0.5">{cat.desc}</p>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {cat.items.map((item, idx) => (
                <AccItem key={item.name} item={item} catId={cat.id} idx={idx} />
              ))}
            </div>
          </div>
        ))}

        {/* CTA commander */}
        <div className="p-8 rounded-2xl border border-[#FF5C00]/20 bg-[#FF5C00]/5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <h3 className="text-xl font-black text-white mb-1">Vous ne trouvez pas ce qu'il vous faut ?</h3>
            <p className="text-sm text-white/50">Contactez-nous — nous pouvons commander des accessoires sur demande.</p>
          </div>
          <div className="flex gap-3 flex-shrink-0">
            <a
              href="tel:+33611367002"
              data-testid="accessoires-call-btn"
              className="flex items-center gap-2 bg-[#FF5C00] hover:bg-[#E05200] text-white text-sm font-semibold px-5 py-3 rounded-full transition-colors"
            >
              <Phone className="w-4 h-4" />
              Nous appeler
            </a>
            <Link
              to="/contact"
              data-testid="accessoires-contact-btn"
              className="flex items-center gap-2 border border-white/20 hover:border-white/40 text-white text-sm font-semibold px-5 py-3 rounded-full transition-colors"
            >
              Nous écrire
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
